import AsyncStorage from '@react-native-async-storage/async-storage';

export type ConsentSettings = {
  analytics: boolean;
  crashReports: boolean;
  updatedAt: string;
};

export type ConsentChoice = Pick<ConsentSettings, 'analytics' | 'crashReports'>;

export const CONSENT_STORAGE_KEY = 'alldrivers.privacy.consent.v1';

const listeners = new Set<(consent: ConsentSettings | null) => void>();
let cachedConsent: ConsentSettings | null | undefined;

const normalizeConsent = (raw: unknown): ConsentSettings | null => {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<ConsentSettings>;
  if (typeof value.analytics !== 'boolean' || typeof value.crashReports !== 'boolean') {
    return null;
  }
  return {
    analytics: value.analytics,
    crashReports: value.crashReports,
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : new Date(0).toISOString(),
  };
};

export const getStoredConsent = async (): Promise<ConsentSettings | null> => {
  if (cachedConsent !== undefined) {
    return cachedConsent;
  }

  try {
    const raw = await AsyncStorage.getItem(CONSENT_STORAGE_KEY);
    cachedConsent = raw ? normalizeConsent(JSON.parse(raw)) : null;
  } catch {
    cachedConsent = null;
  }

  return cachedConsent;
};

export const hasStoredConsent = async (): Promise<boolean> => (await getStoredConsent()) !== null;

export const saveConsent = async (choice: ConsentChoice): Promise<ConsentSettings> => {
  const next: ConsentSettings = {
    analytics: choice.analytics,
    crashReports: choice.crashReports,
    updatedAt: new Date().toISOString(),
  };

  await AsyncStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  cachedConsent = next;
  listeners.forEach((listener) => listener(next));
  return next;
};

export const clearConsentForTests = async (): Promise<void> => {
  await AsyncStorage.removeItem(CONSENT_STORAGE_KEY);
  cachedConsent = null;
  listeners.forEach((listener) => listener(null));
};

// Analytics is OPT-IN: no stored consent means no analytics.
export const canUseAnalytics = async (): Promise<boolean> => (await getStoredConsent())?.analytics === true;
// Crash reports are OPT-OUT (legitimate interest, decisão 2026-07-02):
// undecided (null) counts as allowed; only an explicit false disables them.
export const canUseCrashReports = async (): Promise<boolean> => (await getStoredConsent())?.crashReports !== false;

export const subscribeToConsentChanges = (
  listener: (consent: ConsentSettings | null) => void
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const resetConsentCacheForTests = (): void => {
  cachedConsent = undefined;
  listeners.clear();
};
